import axios from "axios";
import apiAuth from "../../config/axios-interceptor"

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const api = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Sign in.
 *
 * <p>Errors are left to propagate, deliberately. This used to catch everything and return a
 * hand-made `{status: 401}`, which turned every possible failure — a dropped connection, DNS, a
 * timeout, a 500 — into the one answer that means "your password is wrong". An operator was locked
 * out for an afternoon by that: the server's log for the day shows seven sign-in attempts and seven
 * successes, not one rejection, while they were being told their credentials were bad and retyping a
 * password that had never stopped working.
 *
 * <p>Only the server can say a credential is wrong. The caller distinguishes a real rejection from a
 * request that never arrived, and it can only do that if it is given the real error.
 */
export const login = async (username, password) =>
    // Trimmed, because a phone keyboard and a copy-paste both like to add a space, and the lookup is
    // an exact match — a trailing space is a rejected sign-in that looks exactly like a wrong
    // password. The password is NOT trimmed: spaces are legitimate characters in one.
    api.post('/login', {username: (username || '').trim(), password: password});

export const signup = async (username, password) => {
    try {
        return await api.post(
            '/api/signup', {
                username: username,
                password: password
            }
        );
    } catch (error) {
        console.error('Error during API call:', error);
        return {
            status: 401
        }
    }
}

export const sendOtp = async (username) => {
    try {
        return await api.post(
            '/otp/send', {
                phoneNumber: username
            }
        );
    } catch (error) {
        console.error('Error during API call:', error);
        return {
            status: 401
        }
    }
}


export const loginWithOtp = async (username, otp) => {
    try {
        return await api.post(
            '/login/otp', {
                phoneNumber: username,
                otp: otp
            }
        );
    } catch (error) {
        console.error('Error during API call:', error);
        return {
            status: 401
        }
    }
}

export const signupInit = async (username: string) => {
    const response = await api.post("/api/v1/register/init", {
            phoneNumber: username
        }
    );
    return response;
}

export const signupCheckOtp = async (username: string, otp: string) =>
    await api.post("/api/v1/register/checkOTP", {
            phoneNumber: username,
            otp: otp
        }
    );

export const signupUpdatePassword = async (password: string) =>
    await apiAuth.post(
        "/api/v1/register/updatePassword", {
            password: password
        }
    );